/**
 * ConfirmDialogAtom.tsx
 *
 * A generic confirm dialog atom wrapping PrimeReact's ConfirmDialog component
 * with design system styling.
 */

import React from 'react';
import {
  ConfirmDialog,
  ConfirmDialogProps,
  confirmDialog,
} from 'primereact/confirmdialog';
import './ConfirmDialogAtom.scss';

export type ConfirmDialogVariant = 'default' | 'danger' | 'warning';

export interface ConfirmDialogAtomProps extends Omit<ConfirmDialogProps, 'className'> {
  /** Visual variant of the dialog */
  variant?: ConfirmDialogVariant;
  /** Additional CSS class names */
  className?: string;
}

const ConfirmDialogAtom: React.FC<ConfirmDialogAtomProps> = ({
  variant = 'default',
  className = '',
  rejectClassName = '',
  acceptClassName = '',
  ...rest
}) => {
  // Build CSS classes
  const classes = [
    'confirm-dialog-atom',
    `confirm-dialog-atom--variant-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const rejectClasses = ['clear-button', rejectClassName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const acceptClasses = ['Save-button', acceptClassName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return <ConfirmDialog className={classes} rejectClassName={rejectClasses} acceptClassName={acceptClasses} {...rest} />;
};

ConfirmDialogAtom.displayName = 'ConfirmDialogAtom';

/**
 * Options for confirmDialogAtom function.
 *
 * Extends PrimeReact's imperative-call options shape (so callers can pass
 * `onHide`, `defaultFocus`, `position`, etc. without losing type-safety) and
 * adds the design-system `variant` field that maps to a CSS class.
 */
export type ConfirmDialogAtomOptions = Parameters<typeof confirmDialog>[0] & {
  /** Visual variant of the dialog (drives CSS class for theming) */
  variant?: ConfirmDialogVariant;
};

/**
 * Programmatic confirm dialog function with design system styling.
 * Use this to show confirmation dialogs imperatively.
 */
const confirmDialogAtom = (options: ConfirmDialogAtomOptions) => {
  const { variant = 'default', className = '', rejectClassName = '', acceptClassName = '', ...rest } = options;

  const classes = [
    'confirm-dialog-atom',
    `confirm-dialog-atom--variant-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const rejectClasses = ['clear-button', rejectClassName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const acceptClasses = ['Save-button', acceptClassName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return confirmDialog({
    ...rest,
    className: classes,
    rejectClassName: rejectClasses,
    acceptClassName: acceptClasses,
  });
};

export { confirmDialogAtom };
export default ConfirmDialogAtom;
